import { BrokeragePricing } from '../../../slices/marketplace/pricing/models/brokerage-pricing.model';
import { BrokerageType } from '../../../slices/marketplace/pricing/models/brokerage-type.enum';
import { MarketplacePricing } from '../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFeeScheduleItem } from '../../../slices/marketplace/pricing/models/transaction-fee-schedule-item.model';
import {
    FeeCalculatedTableRowValues,
    FeeCalculatedTableValues,
    NullableFeeCalculatedTableRowValues,
} from './FeeCalculatedTableValues';
import { FeeCalculatorInputValues } from './FeeCalculatorInputValues';

export class FeeCalculator {
    constructor(private pricing: MarketplacePricing) {}

    public CalculateDiscountBrokerageFees(
        inputValues: FeeCalculatorInputValues
    ): FeeCalculatedTableValues {
        const brokeragePricing =
            inputValues.brokerageType === BrokerageType.FullService
                ? this.pricing.fullServiceBrokeragePricing
                : this.pricing.discountBrokeragePricing;
        const txFee = this.calculateTxFee(
            inputValues.ltmRevenue,
            brokeragePricing
        );
        const applicationFees = this.calculateApplicationFees(
            inputValues.fteDvms,
            brokeragePricing
        );
        const enterpriseValues = this.calculateEnterpriseValues(
            inputValues,
            brokeragePricing
        );
        const scheduleRow = this.getScheduleRow(inputValues.ltmRevenue, brokeragePricing);
        const processFees = this.calculateProcessFees(
            inputValues,
            brokeragePricing,
            scheduleRow
        );
        const transactionFees = this.calculateTransactionFees(
            txFee,
            enterpriseValues
        );

        const monthlyRetainerFees = this.calculateMonthlyRetainerFees(scheduleRow);

        const creditsValues = this.calculateCredits(
            inputValues,
            brokeragePricing,
            scheduleRow
        );

        const netFeesPaidAtClosing = this.calculateNetFeesPaidAtClosing(
            transactionFees,
            creditsValues
        );

        const totalFeesPaid = this.calculateTotalFeesPaid(
            applicationFees,
            processFees,
            netFeesPaidAtClosing
        );

        const feesAsPercentageOfEnterpriseValue =
            this.calculateFeesAsPercentageOfEnterpriseValue(
                enterpriseValues,
                totalFeesPaid
            );

        return {
            txFee: txFee,
            applicationFee: applicationFees,
            enterpriseValue: enterpriseValues,
            processFees: processFees,
            transactionFees: transactionFees,
            monthlyRetainerFees: monthlyRetainerFees,
            credits: creditsValues,
            netFeesPaidAtClosing: netFeesPaidAtClosing,
            totalFeesPaid: totalFeesPaid,
            feesAsPercentageOfEnterpriseValue:
                feesAsPercentageOfEnterpriseValue,
        };
    }

    private calculateTxFee(
        ltmRevenue: number,
        pricing: BrokeragePricing
    ): number {
        const row = this.getScheduleRow(ltmRevenue, pricing);
        return row?.feePercent ?? 0;
    }

    private getScheduleRow(
        ltmRevenue: number,
        pricing: BrokeragePricing
    ): TransactionFeeScheduleItem | undefined {
        const sorted = [...pricing.transactionFeeSchedule].sort((a, b) => a.revenueMax - b.revenueMax);
        return sorted.find(s => ltmRevenue < s.revenueMax) ?? sorted[sorted.length - 1];
    }

    private calculateApplicationFees(
        fteDvms: number,
        pricing: BrokeragePricing
    ): FeeCalculatedTableRowValues {
        const fee =
            pricing.transactionFees.applicationFee +
            pricing.transactionFees.applicationFeePerDvm * fteDvms;
        return {
            value1: fee,
            value2: fee,
            value3: fee,
            value4: fee,
            value5: fee,
        };
    }

    private calculateEnterpriseValues(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing
    ): FeeCalculatedTableRowValues {
        const mediumRevenue =
            inputValues.ltmRevenue *
            pricing.feeCalculatorSettings.middleOfRangeRevenueMultiple;
        const mediumFteDvmsRangeValue =
            inputValues.fteDvms *
            pricing.feeCalculatorSettings.middleOfRangeValuePerDvm;
        const value3 = Math.max(mediumRevenue, mediumFteDvmsRangeValue);

        const outerPercent =
            (mediumRevenue > mediumFteDvmsRangeValue
                ? pricing.feeCalculatorSettings.outerRevenueMultiplePercent
                : pricing.feeCalculatorSettings.outerValuePerDvmPercent) / 100;
        const innerPercent =
            (mediumRevenue > mediumFteDvmsRangeValue
                ? pricing.feeCalculatorSettings.innerRevenueMultiplePercent
                : pricing.feeCalculatorSettings.innerValuePerDvmPercent) / 100;

        const value1 = (1 - outerPercent) * value3;
        const value2 = (1 - innerPercent) * value3;
        const value4 = (1 + innerPercent) * value3;
        const value5 = (1 + outerPercent) * value3;

        return {
            value1: value1,
            value2: value2,
            value3: value3,
            value4: value4,
            value5: value5,
        };
    }

    private calculateProcessFees(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing,
        scheduleRow: TransactionFeeScheduleItem | undefined
    ): FeeCalculatedTableRowValues {
        const idNdaFee = this.calculateIdNdaFee(inputValues, pricing, scheduleRow);
        let dataNdaFee = this.calculateDataNdaFee(inputValues, pricing, scheduleRow);
        let financialModelFee = this.calculateFinancialModelFee(
            inputValues,
            pricing,
            scheduleRow
        );

        const totalProcessFees = idNdaFee + dataNdaFee + financialModelFee;

        return {
            value1: totalProcessFees,
            value2: totalProcessFees,
            value3: totalProcessFees,
            value4: totalProcessFees,
            value5: totalProcessFees,
        };
    }

    private calculateFinancialModelFee(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing,
        scheduleRow: TransactionFeeScheduleItem | undefined
    ) {
        let fee = scheduleRow?.financialModelFeePerBuyer ?? 0;
        if (inputValues.financialModelFee > pricing.transactionFees.financialModelFirstXCount) {
            fee = fee * (inputValues.financialModelFee - pricing.transactionFees.financialModelFirstXCount + 1);
        }
        return fee;
    }

    private calculateDataNdaFee(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing,
        scheduleRow: TransactionFeeScheduleItem | undefined
    ) {
        let fee = scheduleRow?.dataNdaFeePerBuyer ?? 0;
        if (inputValues.dataNdaFee > pricing.transactionFees.dataNdaFirstXCount) {
            fee = fee * (inputValues.dataNdaFee - pricing.transactionFees.dataNdaFirstXCount + 1);
        }
        return fee;
    }

    private calculateIdNdaFee(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing,
        scheduleRow: TransactionFeeScheduleItem | undefined
    ) {
        let fee = scheduleRow?.idNdaFeePerBuyer ?? 0;
        if (inputValues.idNdaFee > pricing.transactionFees.idNdaFirstXCount) {
            fee = fee * (inputValues.idNdaFee - pricing.transactionFees.idNdaFirstXCount + 1);
        }
        return fee;
    }

    private calculateTransactionFees(
        txFee: number,
        enterpriseValues: FeeCalculatedTableRowValues
    ): FeeCalculatedTableRowValues {
        return {
            value1: (txFee / 100) * enterpriseValues.value1,
            value2: (txFee / 100) * enterpriseValues.value2,
            value3: (txFee / 100) * enterpriseValues.value3,
            value4: (txFee / 100) * enterpriseValues.value4,
            value5: (txFee / 100) * enterpriseValues.value5,
        };
    }

    private calculateMonthlyRetainerFees(
        scheduleRow: TransactionFeeScheduleItem | undefined
    ): NullableFeeCalculatedTableRowValues {
        const retainerFee = scheduleRow?.monthlyRetainerFee ?? null;
        return {
            value1: retainerFee,
            value2: retainerFee,
            value3: retainerFee,
            value4: retainerFee,
            value5: retainerFee,
        };
    }

    private calculateCredits(
        inputValues: FeeCalculatorInputValues,
        pricing: BrokeragePricing,
        scheduleRow: TransactionFeeScheduleItem | undefined
    ): FeeCalculatedTableRowValues {
        const totalMeetingsPrice =
            inputValues.meetingType === 'inPerson'
                ? pricing.transactionFees.buyerMeetingInPersonCredit
                : pricing.transactionFees.buyerMeetingVirtualCredit;
        const meetingsCredits =
            (totalMeetingsPrice *
                inputValues.buyerMeetings *
                pricing.transactionFees.buyerMeetingCreditable) /
            100 * 0.5;
        const applicationFeeCredits =
            ((pricing.transactionFees.applicationFee +
                pricing.transactionFees.applicationFeePerDvm *
                    inputValues.fteDvms) *
                pricing.transactionFees.applicationFeeCreditable) /
            100;

        const idNdaFeeCredits =
            (this.calculateIdNdaFee(inputValues, pricing, scheduleRow) *
                pricing.transactionFees.idNdaFeeCreditable) /
            100;
        const dataNdaFeeCredits =
            (this.calculateDataNdaFee(inputValues, pricing, scheduleRow) *
                pricing.transactionFees.dataNdaFeeCreditable) /
            100;
        const financialModelFeeCredits =
            (this.calculateFinancialModelFee(inputValues, pricing, scheduleRow) *
                pricing.transactionFees.financialModelFeeCreditable) /
            100;
        const retainerFee = scheduleRow?.monthlyRetainerFee;
        const retainerFeeCredits = retainerFee
            ? (retainerFee * (pricing.transactionFees.monthlyRetainerFeeCreditable ?? 0)) / 100
            : 0;

        const totalCredits =
            meetingsCredits +
            applicationFeeCredits +
            idNdaFeeCredits +
            dataNdaFeeCredits +
            financialModelFeeCredits +
            retainerFeeCredits;
        return {
            value1: -totalCredits,
            value2: -totalCredits,
            value3: -totalCredits,
            value4: -totalCredits,
            value5: -totalCredits,
        };
    }

    private calculateNetFeesPaidAtClosing(
        transactionFees: FeeCalculatedTableRowValues,
        creditsValues: FeeCalculatedTableRowValues
    ): FeeCalculatedTableRowValues {
        return {
            value1: transactionFees.value1 + creditsValues.value1,
            value2: transactionFees.value2 + creditsValues.value2,
            value3: transactionFees.value3 + creditsValues.value3,
            value4: transactionFees.value4 + creditsValues.value4,
            value5: transactionFees.value5 + creditsValues.value5,
        };
    }

    private calculateTotalFeesPaid(
        applicationFees: FeeCalculatedTableRowValues,
        processFees: FeeCalculatedTableRowValues,
        netFeesPaidAtClosing: FeeCalculatedTableRowValues
    ): FeeCalculatedTableRowValues {
        return {
            value1:
                applicationFees.value1 +
                processFees.value1 +
                netFeesPaidAtClosing.value1,
            value2:
                applicationFees.value2 +
                processFees.value2 +
                netFeesPaidAtClosing.value2,
            value3:
                applicationFees.value3 +
                processFees.value3 +
                netFeesPaidAtClosing.value3,
            value4:
                applicationFees.value4 +
                processFees.value4 +
                netFeesPaidAtClosing.value4,
            value5:
                applicationFees.value5 +
                processFees.value5 +
                netFeesPaidAtClosing.value5,
        };
    }

    private calculateFeesAsPercentageOfEnterpriseValue(
        enterpriseValues: FeeCalculatedTableRowValues,
        totalFeesPaid: FeeCalculatedTableRowValues
    ): FeeCalculatedTableRowValues {
        return {
            value1: (totalFeesPaid.value1 / enterpriseValues.value1) * 100,
            value2: (totalFeesPaid.value2 / enterpriseValues.value2) * 100,
            value3: (totalFeesPaid.value3 / enterpriseValues.value3) * 100,
            value4: (totalFeesPaid.value4 / enterpriseValues.value4) * 100,
            value5: (totalFeesPaid.value5 / enterpriseValues.value5) * 100,
        };
    }
}
